import { cacheDirectory } from "expo-file-system/legacy";
import { SaveFormat } from "expo-image-manipulator";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import {
  DocumentExtensions,
  DocumentMimeType,
  DocumentType,
} from "@/constants/Documents";
import { formatISODate } from "@/utils/date.util";
import { getPreferredFullName } from "@/utils/user.util";

const getFileExtension = (fileName?: string): string | undefined => {
  if (isNil(fileName)) {
    return undefined;
  }

  const filePathWithoutQuery = fileName.split("?")[0];
  const fileNameArray = filePathWithoutQuery.split(".");
  const lastElement = fileNameArray.pop();

  if (isEmpty(lastElement) || isEmpty(fileNameArray)) {
    return undefined;
  }

  return lastElement;
};

const getDocumentTypeFromFileName = (fileName?: string): DocumentType => {
  const fileExtension = getFileExtension(fileName?.toLowerCase());

  if (isNil(fileExtension)) {
    return DocumentType.Generic;
  }

  if (DocumentExtensions.PDF.includes(fileExtension)) {
    return DocumentType.PDF;
  }

  if (DocumentExtensions.Word.includes(fileExtension)) {
    return DocumentType.Word;
  }

  if (DocumentExtensions.Excel.includes(fileExtension)) {
    return DocumentType.Excel;
  }

  if (DocumentExtensions.Image.includes(fileExtension)) {
    return DocumentType.Image;
  }

  return DocumentType.Generic;
};

const getDocumentSenderName = ({
  author,
  isSentByAppUser,
}: {
  author: PatientDocumentAuthor;
  isSentByAppUser: boolean;
}): string => {
  if (isSentByAppUser) {
    return "you";
  }

  return getPreferredFullName({
    chosenName: author.chosenName,
    firstName: author.firstName,
    lastName: author.lastName,
  });
};

const getDocumentSentDateDescription = ({
  author,
  dateString,
  isSentByAppUser,
}: {
  author: PatientDocumentAuthor;
  dateString: string;
  isSentByAppUser: boolean;
}): string => {
  const formattedDate = formatISODate({
    dateString,
    format: DateFormat.shortMonthDay,
  });

  const senderName = getDocumentSenderName({ author, isSentByAppUser });

  return `Sent by ${senderName} on ${formattedDate}`;
};

interface GeneratePDFFilePathParams {
  extension: string;
  title: string;
}
/**
 * Encodes the document path for a PDF file using the provided title and extension.
 * @param extension - The file extension of the document.
 * @param title - The title of the document.
 *
 * @returns The encoded document path.
 *
 * @example
 * ```ts
 * const path = encodeDocumentPath({ extension: "pdf", title: "My Document Title" });
 * console.log(path); // "/mock/cache/directory/My%20Document%20Title.pdf"
 * ```
 */
const encodeDocumentPath = ({
  extension,
  title,
}: GeneratePDFFilePathParams) => {
  if (isEmpty(extension) || isEmpty(title)) {
    return undefined;
  }

  const encodedFilename = `${encodeURIComponent(title)}.${extension}`;
  return `${cacheDirectory}${encodedFilename}`;
};

const getSMTImageMimeType = (saveFormat: SaveFormat): string | undefined => {
  switch (saveFormat) {
    case SaveFormat.JPEG:
      return DocumentMimeType.JPEG;
    case SaveFormat.PNG:
      return DocumentMimeType.PNG;
    case SaveFormat.WEBP:
      return DocumentMimeType.WEBP;
    default:
      return undefined;
  }
};

export {
  encodeDocumentPath,
  getDocumentSentDateDescription,
  getDocumentTypeFromFileName,
  getFileExtension,
  getSMTImageMimeType,
};
